import { Injectable } from '@angular/core';
import { OLMapService } from './ol-map.service';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { BBOX, DEFAULT_EXTENT, FIX_FILTERS, LABEL_ATLAS_FIX } from 'src/assets/constants/constants';
import { PointDetails } from 'src/app/models/pointDetails';
import { OlAtlasBaseService } from './ol-atlas-base.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiFilterValue } from 'src/app/datasets/api-filter-value.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { checkCorrectParams, checkQueryParameters } from 'src/app/utils/helpers';
import { AreaLevel } from 'src/app/models/domain/arealevel-enum';
import { TranslationHelperService } from '../translations/translation-helper.service';
import { FeatureCollection } from 'src/app/utils/geojson';
import { Coordinate } from 'ol/coordinate';

@Injectable({
    providedIn: 'root',
})
export class AtlasFixService extends OlAtlasBaseService {
    pointDetails: PointDetails;
    polygonSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    _lang: any;
    statSecDetailsSource = new TileWMS({
        url: `${environment.geoServer.baseUrl}/${environment.geoServer.dp_landline_details}/wms`,
        params: {
            LAYERS: environment.geoServer.statsec_details,
            FORMAT: 'image/png',
            STYLES: '',
            VERSION: '1.1.1',
        },
        serverType: 'geoserver',
    });

    constructor(
        private translationHelperService: TranslationHelperService,
        private http: HttpClient,
        public olMapService: OLMapService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        super(olMapService);
        translationHelperService.getLanguage().subscribe((lang) => {
            this._lang = lang;
        });
    }

    mapOperations(event: MapBrowserEvent<MouseEvent>) {
        this.olMapService.details = [];
        this.removeVectorLayers();
        return this.createJoinEndpoints(event);
    }

    createJoinEndpoints(event: MapBrowserEvent<MouseEvent>): { layer: any; featureCollection: Observable<Object> } {
        const view = this.olMapService.map.getView();
        const zoom = view.getZoom();
        const resolution = view.getResolution();
        const projection = view.getProjection();
        const coordinate = event.coordinate;
        if (zoom > 15.5) {
            const source = this.statSecDetailsSource;
            const url = source.getFeatureInfoUrl(coordinate, resolution, projection.getCode(), {
                INFO_FORMAT: 'application/json',
                FEATURE_COUNT: 10,
            });
            return {
                layer: this.statSecDetailsSource.getParams().LAYERS,
                featureCollection: this.http.get(url),
            };
        } else {
            const wfsUrl = this.buildWFSUrl(coordinate, projection.getCode());
            return {
                layer: this.getLayerName(),
                featureCollection: this.http.get<FeatureCollection>(wfsUrl),
            };
        }
    }
    buildWFSUrl(coord: Coordinate, projection: string): string {
        const [x, y] = coord;
        const layerName = `${environment.geoServer.dp_landline_overview_merged}:${environment.geoServer.landline_per_level}`;
        const srs = projection || 'EPSG:3857';
        const filterProperty = this.getfilterProperty();
        const cqlFilter = encodeURIComponent(`
            level IN ('statsec','municipality','province','district','region','country') AND filterproperty ='${filterProperty}' AND
            INTERSECTS(geom, POINT(${x} ${y}))
        `);

        return (
            `${environment.geoServer.baseUrl}/wfs?` +
            `service=WFS&version=1.1.0&request=GetFeature&typeName=${layerName}` +
            `&outputFormat=application/json&srsName=${srs}&CQL_FILTER=${cqlFilter}`
        );
    }

    getLayerDetails(feature: Feature<Geometry>) {
        this.olMapService.detailsLoading = true;
        feature
            .getKeys()
            .sort()
            .forEach((key) => {
                if (LABEL_ATLAS_FIX.includes(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: key.includes(LABEL_ATLAS_FIX[0]) ? 'area' : key,
                        value: feature.get(key),
                    });
                }
            });
        this.olMapService.details.push({
            label: 'name',
            value: feature.get('Name'),
        });
        const style = this.getStyleName();
        feature
            .getKeys()
            .sort()
            .forEach((key) => {
                const column = style.replace('households', 'H').replace('population', 'P');
                if (!column.localeCompare(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: 'cover',
                        ratio: feature.get(key),
                    });
                }
            });
        this.olMapService.detailsLoading = false;
    }
    getLayerName(): string {
        const layerName = this.olMapService.filter.get(FIX_FILTERS[1]);
        if (!layerName) {
            return;
        }
        return layerName.toLowerCase();
    }
    getStyleName(): string {
        const speed = this.olMapService.filter.get(FIX_FILTERS[0]);
        const coverType = this.olMapService.filter.get(FIX_FILTERS[2]);
        const style = `${coverType}_${speed}`;
        return style;
    }

    getfilterProperty(): string {
        const speed = this.olMapService.filter.get(FIX_FILTERS[0]);
        const coverType = this.olMapService.filter.get(FIX_FILTERS[2])[0].toUpperCase();
        const style = `${coverType}_${speed}`;
        return style;
    }

    getCqlFilter(): string {
        const layerName = this.getLayerName();
        const filterProperty = this.getfilterProperty();
        const cqlFilter = `level = '${layerName}' and filterproperty ='${filterProperty}'`;
        return cqlFilter;
    }

    loadLayer(): void {
        this.removeVectorLayers();
        const layerName = this.getLayerName();
        const cqlFilter = this.getCqlFilter();
        let existingLayer = undefined;
        const selectedLayerName = `${layerName}_${cqlFilter}`;
        existingLayer = this.olMapService.layers.find((layer) => layer.name === selectedLayerName);
        if (existingLayer === undefined) {
            const workspace = environment.geoServer.dp_landline_overview_merged;
            const tileLayer = new TileLayer({
                source: new TileWMS({
                    url: `${environment.geoServer.baseUrl}/${workspace}/wms`,
                    params: {
                        LAYERS: `${workspace}:${environment.geoServer.landline_per_level}`,
                        FORMAT: 'image/png',
                        TILED: true,
                        CQL_FILTER: cqlFilter,
                        VERSION: '1.1.1',
                    },
                    serverType: 'geoserver',
                }),
                opacity: 0.6,
            });
            tileLayer.getSource().updateParams({
                CQL_FILTER: cqlFilter,
            });
            this.olMapService.map.addLayer(tileLayer);
            this.olMapService.layers.push({
                name: selectedLayerName,
                layer: tileLayer,
                workspace: environment.geoServer.dp_landline_overview_merged,
            });
            tileLayer.set('layerdetails', {
                name: selectedLayerName,
                workspace: environment.geoServer.dp_landline_overview_merged,
            });
        } else {
            existingLayer.layer.setVisible(true);
        }
        this.olMapService.layers.forEach((visibleLayer) => {
            if (
                visibleLayer.workspace === environment.geoServer.dp_landline_overview_merged &&
                visibleLayer.name !== environment.geoServer.statsec_details &&
                visibleLayer.name !== selectedLayerName
            ) {
                visibleLayer.layer.setVisible(false);
            }
        });
        this.changeUrl();
    }

    loadPoint(apiFilterValue: ApiFilterValue) {
        const existingLayer = this.olMapService.layers.find((point) => point.name == environment.geoServer.statsec_details);
        if (!existingLayer) {
            const tileLayer = new TileLayer({
                source: this.statSecDetailsSource,
                opacity: 0.6,
            });
            this.olMapService.map.addLayer(tileLayer);
            this.olMapService.layers.push({
                name: environment.geoServer.statsec_details,
                layer: tileLayer,
                workspace: environment.geoServer.dp_landline_details,
            });
            tileLayer.set('layerdetails', {
                name: environment.geoServer.statsec_details,
                workspace: environment.geoServer.dp_landline_details,
            });
        } else {
            existingLayer.layer.setVisible(true);
        }
        this.olMapService.layers.forEach((visibleLayer) => {
            if (
                visibleLayer.workspace !== environment.geoServer.dp_landline_details &&
                visibleLayer.workspace !== environment.geoServer.dp_landline_overview_merged
            ) {
                visibleLayer.layer.setVisible(false);
            }
        });
    }
    setFilter(): void {
        this.loadLayer();
    }
    updateDetails(feature: Feature<Geometry>) {
        const layerName = this.getLayerName();
        const id = feature.getId().toString().split('.');

        if (!id[0].includes(layerName)) {
            return;
        }

        this.pointDetails = [];
        this.olMapService.detailsLoading = true;
        this.getLayerDetails(feature);

        this.olMapService.detailsLoading = false;
    }

    updateStatSecDetails(features: Feature<Geometry>[]) {
        const layerName = this.getLayerName();
        const id = features.map((feature) => feature.getId().toString().split('.'));

        if (!id[0].includes(layerName)) {
            return;
        }
        if (!id[1].includes(AreaLevel.MUNICIPALITY)) {
            this.pointDetails = [];
            this.olMapService.detailsLoading = true;
            this.getLayerDetails(features[0]);

            this.olMapService.detailsLoading = false;
            return;
        }

        this.pointDetails = [];
        this.olMapService.detailsLoading = true;
        this.getStatSecLayerDetails(features);

        this.olMapService.detailsLoading = false;
    }

    getStatSecLayerDetails(features: Feature<Geometry>[]) {
        const feature = features[0];
        this.olMapService.details.push({
            label: '',
            level: 'MUNICIPALITY',
            value: features[1].get('Name'),
        });
        this.olMapService.detailsLoading = true;
        feature
            .getKeys()
            .sort()
            .forEach((key) => {
                if (LABEL_ATLAS_FIX.includes(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: key.includes(LABEL_ATLAS_FIX[0]) ? 'area' : key,
                        value: feature.get(key),
                    });
                }
            });
        this.olMapService.details.push({
            label: 'name',
            value: feature.get('Name'),
        });
        const style = this.getStyleName();
        feature
            .getKeys()
            .sort()
            .forEach((key) => {
                const column = style.replace('households', 'H').replace('population', 'P');
                if (!column.localeCompare(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: 'cover',
                        ratio: feature.get(key),
                    });
                }
            });
        this.olMapService.detailsLoading = false;
    }

    updatePointDetails(features: Feature<Geometry>[]) {
        const layerName = this.getLayerName();
        features.forEach((feature, i) => {
            const id = feature.getId().toString().split('.');

            if (!id[0].includes(layerName)) {
                if (i === 0) {
                    this.pointDetails = [];
                    this.olMapService.detailsLoading = true;
                }

                feature.setProperties({ Provider: id.shift() });
                this.pointDetails.push(feature.getProperties());
            }
        });

        this.olMapService.detailsLoading = false;
    }

    setDefaultFilter() {
        if (Object.keys(this.olMapService.mapConfig).length === 0) {
            return;
        }
        const haveAllQueryParams = checkQueryParameters(this.olMapService.mapConfig, FIX_FILTERS);
        if (!haveAllQueryParams) {
            return;
        }

        const isCorrectParams = checkCorrectParams(this.olMapService.filters, this.olMapService.mapConfig, FIX_FILTERS);
        if (isCorrectParams.some((cp) => cp == false)) {
            return;
        }

        this.olMapService.filter.set(FIX_FILTERS[0], this.olMapService.mapConfig[FIX_FILTERS[0]]);
        this.olMapService.filter.set(FIX_FILTERS[1], this.olMapService.mapConfig[FIX_FILTERS[1]]);
        this.olMapService.filter.set(FIX_FILTERS[2], this.olMapService.mapConfig[FIX_FILTERS[2]]);
        const bBoxArr = this.olMapService.mapConfig[BBOX] ? this.olMapService.mapConfig[BBOX].split(',').map((bbox) => Number(bbox)) : '';
        if (bBoxArr != '') {
            this.olMapService.map.getView().fit(bBoxArr);
        }
    }

    changeUrl() {
        // Calculate bbox safely to prevent MAX_INT overflow
        const mapSize = this.olMapService.map.getSize();
        let bboxValue: string;

        if (mapSize && mapSize[0] > 0 && mapSize[1] > 0) {
            try {
                const extent = this.olMapService.map.getView().calculateExtent(mapSize);
                // Validate extent coordinates are reasonable
                if (extent.every(coord => isFinite(coord) && Math.abs(coord) < 90000000)) {
                    bboxValue = extent.toString();
                } else {
                    console.warn('[Atlas Fix] Invalid extent calculated, using default');
                    bboxValue = DEFAULT_EXTENT.toString();
                }
            } catch (e) {
                console.error('[Atlas Fix] Error calculating extent:', e);
                bboxValue = DEFAULT_EXTENT.toString();
            }
        } else {
            console.warn('[Atlas Fix] Invalid map size, using default extent');
            bboxValue = DEFAULT_EXTENT.toString();
        }

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                [FIX_FILTERS[0]]: this.olMapService.filter.get(FIX_FILTERS[0]),
                [FIX_FILTERS[1]]: this.olMapService.filter.get(FIX_FILTERS[1]),
                [FIX_FILTERS[2]]: this.olMapService.filter.get(FIX_FILTERS[2]),
                [BBOX]: bboxValue,
            },
            replaceUrl: true,
        });
    }
}
